'use client';
import { atom, useAtom } from 'jotai';

import type {
	GeolocationDetails,
	LocationType,
	LocationV2,
} from '@dte/otw-azure-functions/src/common/interfaces/api/Location';
import {
	isGLNLocationType,
	isNonPremiseLocationType,
} from '@dte/otw-azure-functions/src/common/reports/customerReport/symptomsToCallCodeV3/isNonPremiseLocation';
import { copyJson } from '@dte/otw-utils-core/src/objects/copyJson';
import { deepMergePartial } from '@dte/otw-utils-core/src/objects/deepMergePartial';

import { isValidLatitude, isValidLongitude } from '../../../../../components/form/inputs/gln/isValidGLNString';
import { workingDraftReportAtom } from '../_atoms/draftReport/draftReportAtoms';

const defaultLocation: LocationV2 = {
	version: 2,
	premiseId: undefined,
	gps: undefined,
};

export const locationAtom = atom(
	(get) => {
		const draftReport = get(workingDraftReportAtom);
		return draftReport?.location ?? defaultLocation;
	},
	(get, set, value: LocationV2) => {
		const draftReport = get(workingDraftReportAtom);
		const newValue = copyJson(draftReport);
		newValue.location = deepMergePartial([defaultLocation, value]);
		set(workingDraftReportAtom, newValue);
	},
);

export const locationGpsAtom = atom(
	(get) => get(locationAtom)?.gps,
	(get, set, gps: GeolocationDetails | undefined) => {
		const location = get(locationAtom);
		const updatedLocation = copyJson(location);

		// Make sure GPS values are valid
		let gpsValue: GeolocationDetails | undefined = gps;
		const longitude = gps?.longitude;
		if (!isValidLongitude(longitude)) {
			gpsValue = undefined;
		}
		const latitude = gps?.latitude;
		if (!isValidLatitude(latitude)) {
			gpsValue = undefined;
		}

		updatedLocation.gps = gpsValue;

		set(locationAtom, updatedLocation);
	},
);

export const locationTypeAtom = atom(
	(get) => {
		return get(locationAtom)?.type;
	},
	(get, set, value: LocationType) => {
		const location = get(locationAtom);
		const newValue = copyJson(location);
		newValue.type = value;
		set(locationAtom, newValue);
	},
);
locationTypeAtom.debugLabel = 'locationTypeAtom';

const nonPremiseLocationAtom = atom((get) => {
	const location = get(locationAtom);
	return isNonPremiseLocationType(location?.type);
});

export function useIsNonPremiseReport(): boolean {
	const [nonPremise] = useAtom(nonPremiseLocationAtom);
	return nonPremise;
}

const GLNLocationAtom = atom((get) => {
	const location = get(locationAtom);
	return isGLNLocationType(location?.type);
});

export function useIsGLNReport(): boolean {
	const [glnLocation] = useAtom(GLNLocationAtom);
	return glnLocation;
}
